/**
 * 隐私合规与数据脱敏模块
 * 遵循新加坡 PDPA (Personal Data Protection Act) 要求
 */

/**
 * PDPA 合规检查清单
 * 用于审计数据收集实践是否符合 PDPA 要求
 */
export const PDPAChecklist = {
  consent: {
    id: 'consent-obtained',
    description: '是否在收集个人数据前获得用户明确同意',
    required: true,
    verify: () => localStorage.getItem('fsm_analytics_consent') === 'true'
  },
  dataMinimization: {
    id: 'data-minimization',
    description: '是否避免收集精确位置、姓名、联系方式等 PII',
    required: true,
    verify: (data) => !data || !Object.values(data).some(v =>
      typeof v === 'string' && (
        /^[\w.-]+@[\w.-]+\.\w+$/.test(v) ||
        /^\+?\d{7,15}$/.test(v) ||
        /^\d{6}$/.test(v)
      )
    )
  }
};

/**
 * 运行 PDPA 合规检查
 * @param {Object} eventData - 待发送的事件数据
 * @returns {Object} { passed: boolean, violations: string[] }
 */
export function runPDPACheck(eventData = null) {
  const violations = [];

  Object.values(PDPAChecklist).forEach(check => {
    if (check.required && !check.verify(eventData)) {
      violations.push(`${check.id}: ${check.description}`);
    }
  });

  return {
    passed: violations.length === 0,
    violations
  };
}

/**
 * 坐标模糊化
 * 将精确经纬度转换为区域级别坐标（约 1km 精度）
 * 不发送精确位置，仅保留区域级信息用于分析
 *
 * @param {number} lat - 纬度
 * @param {number} lng - 经度
 * @param {number} precision - 小数位数（默认 2，约 1km 精度）
 * @returns {{lat: number, lng: number}} 模糊化后的坐标
 */
export function obfuscateCoordinates(lat, lng, precision = 2) {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    throw new TypeError('Coordinates must be numbers');
  }

  const factor = Math.pow(10, precision);
  return {
    lat: Math.round(lat * factor) / factor,
    lng: Math.round(lng * factor) / factor
  };
}

/**
 * 移除 PII (Personally Identifiable Information)
 * 从数据对象中递归删除可能的个人身份信息
 *
 * @param {Object} data - 原始数据
 * @returns {Object} 清理后的数据
 */
export function removePII(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const piiPatterns = [
    { key: /email|e-mail|mail/i, type: 'email' },
    { key: /phone|tel|mobile|contact/i, type: 'phone' },
    { key: /^(?:user)?name$|^(?:full|user)?name$/i, type: 'name' },
    { key: /address|addr|地址/i, type: 'address' },
    { key: /nric|fin|passport|id.?number/i, type: 'id' },
    { key: /ip.?address|ipaddr/i, type: 'ip' }
  ];

  const cleaned = {};

  for (const [key, value] of Object.entries(data)) {
    const matchedPattern = piiPatterns.find(pattern => pattern.key.test(key));

    if (matchedPattern) {
      cleaned[key] = `[REDACTED_${matchedPattern.type.toUpperCase()}]`;
    } else if (typeof value === 'object' && value !== null) {
      cleaned[key] = removePII(value);
    } else if (typeof value === 'string') {
      cleaned[key] = redactSensitiveString(value);
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned;
}

/**
 * 脱敏敏感字符串
 * 检测并替换字符串中的敏感信息
 *
 * @param {string} str - 输入字符串
 * @returns {string} 脱敏后的字符串
 */
function redactSensitiveString(str) {
  if (typeof str !== 'string') return str;

  return str
    .replace(/[\w.-]+@[\w.-]+\.\w+/g, '[REDACTED_EMAIL]')
    .replace(/\+?\d{7,15}/g, '[REDACTED_PHONE]')
    .replace(/\b[A-Z]{1,2}\d{7}[A-Z]\b/g, '[REDACTED_ID]')
    .replace(/\b\d{6}\b/g, '[REDACTED_POSTAL]');
}

/**
 * 生成匿名会话 ID
 * 用于关联同一用户会话中的事件，但不标识用户身份
 *
 * @returns {string} 匿名会话 ID
 */
export function generateAnonymousSessionId() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 10);
  return `sess_${timestamp}_${random}`;
}

/**
 * 获取或创建匿名会话 ID
 * 存储在 sessionStorage 中，浏览器关闭后清除
 *
 * @returns {string} 匿名会话 ID
 */
export function getAnonymousSessionId() {
  let sessionId = sessionStorage.getItem('fsm_analytics_session');
  if (!sessionId) {
    sessionId = generateAnonymousSessionId();
    sessionStorage.setItem('fsm_analytics_session', sessionId);
  }
  return sessionId;
}

/**
 * 清除所有分析相关存储
 * 用户撤销同意时调用
 */
export function clearAnalyticsStorage() {
  localStorage.removeItem('fsm_analytics_consent');
  sessionStorage.removeItem('fsm_analytics_session');
}

/**
 * 隐私政策相关工具函数
 */
export const privacyPolicy = {
  /**
   * 获取数据收集范围说明
   * @returns {Object} 收集范围说明
   */
  getCollectionScope() {
    return {
      collected: [
        '匿名页面浏览行为',
        '地图区域级别点击（不含精确坐标）',
        '功能按钮点击（如预约咨询）',
        '浏览器语言偏好',
        '设备类型（桌面/移动）'
      ],
      notCollected: [
        '精确 GPS 坐标',
        '姓名、邮箱、电话等个人信息',
        'IP 地址',
        '设备唯一标识符',
        'Cookie 追踪跨站行为'
      ],
      purpose: '用于改进地图功能和用户体验，所有数据均为匿名聚合分析',
      retention: '数据保留不超过 26 个月',
      thirdParty: '使用 Plausible Analytics（欧盟托管、GDPR 合规、不使用 Cookie）'
    };
  },

  /**
   * 生成隐私政策摘要文本
   * @param {string} lang - 语言代码
   * @returns {string} 隐私政策摘要
   */
  getSummary(lang = 'zh') {
    const summaries = {
      zh: '我们重视您的隐私。本网站仅收集匿名行为数据用于改进服务：\n• 不收集精确位置，仅记录区域级别信息\n• 不收集姓名、邮箱、电话等个人信息\n• 不使用 Cookie 追踪您的跨站行为\n• 使用 Plausible Analytics，数据托管于欧盟，符合 GDPR 标准\n• 您可以随时在页面底部撤销同意',

      en: 'We value your privacy. This site only collects anonymous usage data:\n• No precise location, only region-level information\n• No personal information like name, email, or phone\n• No cross-site tracking cookies\n• Uses Plausible Analytics, EU-hosted, GDPR-compliant\n• You can withdraw consent anytime via the footer link'
    };

    return summaries[lang] || summaries.zh;
  }
};
